import React, { useCallback, useEffect, useState } from 'react';
import MobileVerificationGate, { mobileVerificationEnforced } from './MobileVerificationGate';
import { supabase } from './supabase';
import NotificationRuntime from '../notifications/NotificationRuntime';
import RemsDashboard from '../rems/RemsDashboard';

const REMS_SHARED_EMAIL = '24rem@metrofire.ca.gov';

function isSharedRems(profile) {
  return String(profile?.email || '').trim().toLowerCase() === REMS_SHARED_EMAIL;
}

function isIndividualRems(profile) {
  return ['rems_temporary', 'rems_permanent'].includes(String(profile?.member_kind || ''));
}

function isExemptPath() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/app-review-demo' || path.startsWith('/account/');
}

function isRootPath() {
  if (typeof window === 'undefined') return false;
  return (window.location.pathname.replace(/\/+$/, '') || '/') === '/';
}

export default function MemberMobileVerificationBoundary({ children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [checking, setChecking] = useState(true);

  const loadProfile = useCallback(async activeSession => {
    if (!activeSession) {
      setSession(null);
      setProfile(null);
      setChecking(false);
      return;
    }
    setSession(activeSession);
    const { data, error } = await supabase.rpc('refresh_member_approval');
    if (!error) setProfile(data || null);
    else setProfile(null);
    setChecking(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      loadProfile(data?.session || null);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      setChecking(true);
      loadProfile(nextSession || null);
    });
    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, [loadProfile]);

  if (!mobileVerificationEnforced() || isExemptPath()) return children;

  if (checking || session === undefined) {
    return <div className="mobile-verify-root" aria-label="Checking Line Medic security" />;
  }

  if (
    !session
    || !profile
    || !profile.approved
    || profile.disabled
    || (!profile.activated_at && !isSharedRems(profile))
  ) {
    return children;
  }

  return (
    <MobileVerificationGate profile={profile} onProfileChanged={setProfile}>
      <NotificationRuntime profile={profile} />
      {isIndividualRems(profile) && isRootPath() ? <RemsDashboard profile={profile} /> : children}
    </MobileVerificationGate>
  );
}
